import styles from "./interaction-icon.module.scss";
import type { Factory, IGroupProps, IProps, ISkeletonProps } from "./interaction-icon.types";
import { polymorphicComponentWrapper } from "../../utilities";
import { Center } from "../center";
import { Icon } from "../icon";
import { getInteractionColor, getInteractionIcon } from "./interaction-icon.utilities";
import { Skeleton } from "../skeleton";
import classNames from "classnames";
import { Group } from "../group";
import { Children } from "react";
import { kebabCase } from "lodash";

/**
 * Interaction icon component for displaying icons representing different interaction types.
 * @param {string} props.type - The type of interaction (e.g., "call", "email", "chat").
 * @param {string} props.size - The size of the icon (default: "lg").
 * @returns {JSX.Element} The InteractionIcon component.
 */
const InteractionIcon = polymorphicComponentWrapper<Factory, IProps>(
    "InteractionIcon",
    ({ type, size = "lg", ...otherProps }, ref) => {
        return (
            <Center
                ref={ref}
                bdrs={"50%"}
                data-type={type}
                bg={`var(--surface-${getInteractionColor(type)}-transparent-color)`}
                c={`var(--surface-${getInteractionColor(type)}-prominent-color)`}
                classNames={{ root: styles["root"] }}
                style={{ "--ai-size": `var(--ai-size-${size})` }}
                data-testid={`interaction-icon-${kebabCase(type)}`}
                {...otherProps}
            >
                <Icon icon={getInteractionIcon(type)} />
            </Center>
        );
    }
);
/**
 * Skeleton loader component for InteractionIcon.
 * @returns {JSX.Element} The InteractionIconSkeleton component.
 */
const InteractionIconSkeleton = (props: ISkeletonProps) => {
    return <Skeleton circle className={styles["skeleton"]} {...props} />;
};
/**
 * Group component for displaying multiple interaction icons together with overlapping effect.
 * @param {string} props.className - Additional CSS class names.
 * @param {React.ReactNode} props.children - InteractionIcon components to group together.
 * @returns {JSX.Element} The InteractionIconGroup component.
 */
const InteractionIconGroup = ({ className, children, ...otherProps }: IGroupProps) => {
    return (
        <Group gap="0" className={classNames(styles["group"], className)} {...otherProps}>
            {Children.map(children, (child, index) => (
                <Center className={styles["iconWrapper"]} bdrs="xl" left={index * -8} style={{ zIndex: index + 1 }}>
                    {child}
                </Center>
            ))}
        </Group>
    );
};

InteractionIcon.Skeleton = InteractionIconSkeleton;
InteractionIcon.Group = InteractionIconGroup;

export { InteractionIcon, InteractionIconSkeleton, InteractionIconGroup };
